import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAction } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { notifyError } from "@/lib/notify-error";
import {
  ROLE_LABELS,
  describeBillingConsequence,
  describeRoleChange,
  getMemberChangeErrorMessage,
  type OrgRole,
  type SeatBillingPreview,
} from "./member-change-copy";
import type { OrganizationMember } from "./member-rows";
import { SeatBillingNotice } from "./seat-billing-notice";

export type MemberChange =
  | { kind: "remove"; userId: Id<"users"> }
  | { kind: "change_role"; userId: Id<"users">; role: OrgRole }
  | { kind: "transfer_ownership"; userId: Id<"users"> };

export type PendingChange = { change: MemberChange; member: OrganizationMember };

function changeTitle(pending: PendingChange) {
  const who = pending.member.name?.trim() || pending.member.email;
  switch (pending.change.kind) {
    case "remove":
      return `Remove ${who}?`;
    case "transfer_ownership":
      return `Transfer ownership to ${who}?`;
    case "change_role":
      return `Make ${who} ${ROLE_LABELS[pending.change.role].toLowerCase()}?`;
  }
}

function confirmLabel(change: MemberChange) {
  if (change.kind === "remove") return "Remove member";
  if (change.kind === "transfer_ownership") return "Transfer ownership";
  return "Change role";
}

/** Reviewer → member goes through CON-428's explicit promotion, which owns that billing consent. */
function isReviewerPromotion(pending: PendingChange) {
  return pending.change.kind === "change_role" && pending.change.role === "member" && pending.member.orgRole === "reviewer";
}

type ChangePreview =
  | { allowed: false; message: string }
  | (SeatBillingPreview & { allowed: true; requiresBillableSeatAcknowledgement: boolean });

function useChangePreview(pending: PendingChange): { preview: ChangePreview | undefined; isPending: boolean } {
  const { change } = pending;
  const promotion = isReviewerPromotion(pending);
  const memberChange = useQuery(convexQuery(api.members.previewMemberChange, promotion ? "skip" : { change }));
  const reviewerPromotion = useQuery(
    convexQuery(api.reviewers.getReviewerPromotionPreview, promotion ? { userId: change.userId } : "skip")
  );
  if (!promotion) return { preview: memberChange.data, isPending: memberChange.isPending };
  const data = reviewerPromotion.data;
  return {
    isPending: reviewerPromotion.isPending,
    preview: data && { ...data, allowed: true, requiresBillableSeatAcknowledgement: true },
  };
}

/**
 * Confirms a role change, removal, or ownership transfer. Every change is checked and previewed
 * (including its billing consequence) by the server before it runs.
 */
export function MemberChangeDialog(props: { pending: PendingChange; onClose: () => void }) {
  const { change } = props.pending;
  const { preview, isPending } = useChangePreview(props.pending);
  const removeMember = useAction(api.memberActions.removeMember);
  const changeMemberRole = useAction(api.memberActions.changeMemberRole);
  const transferOwnership = useAction(api.memberActions.transferOwnership);
  const promoteReviewerToMember = useAction(api.authActions.promoteReviewerToMember);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const blocked = preview?.allowed === true && preview.billingConsequence === "blocked_pending_cancellation";
  const needsAcknowledgement = preview?.allowed === true && preview.requiresBillableSeatAcknowledgement && !blocked;
  const canConfirm = preview?.allowed === true && !blocked && (!needsAcknowledgement || acknowledged) && !isSaving;

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      if (isReviewerPromotion(props.pending)) {
        await promoteReviewerToMember({ userId: change.userId, acknowledgeBillableSeat: acknowledged });
        toast.success(`${props.pending.member.email} is now a member.`);
      } else if (change.kind === "remove") {
        await removeMember({ userId: change.userId });
        toast.success(`${props.pending.member.email} was removed and signed out.`);
      } else if (change.kind === "transfer_ownership") {
        await transferOwnership({ userId: change.userId });
        toast.success(`${props.pending.member.email} is now an owner. You are now an admin.`);
      } else {
        await changeMemberRole({ userId: change.userId, role: change.role, acknowledgeBillableSeat: acknowledged });
        toast.success(`${props.pending.member.email} is now ${ROLE_LABELS[change.role].toLowerCase()}.`);
      }
      props.onClose();
    } catch (error) {
      notifyError(error, getMemberChangeErrorMessage(error, "The change could not be saved. Try again."));
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !isSaving && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{changeTitle(props.pending)}</DialogTitle>
          <DialogDescription>
            {change.kind === "remove"
              ? "They lose access right away, including on devices where they are signed in. You can invite them again later."
              : change.kind === "change_role"
                ? describeRoleChange(props.pending.member.orgRole, change.role)
                : "They become an owner and you become an admin."}
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Checking this change...
          </div>
        ) : preview?.allowed === false ? (
          <p className="text-sm font-medium text-destructive" role="alert">{preview.message}</p>
        ) : preview ? (
          <div className="space-y-3">
            <SeatBillingNotice message={describeBillingConsequence(preview)} blocked={blocked} />
            {needsAcknowledgement ? (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(checked) => setAcknowledged(checked === true)}
                  className="mt-0.5"
                />
                <span>I understand {props.pending.member.name?.trim() || props.pending.member.email} will use a paid seat.</span>
              </label>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={props.onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant={change.kind === "remove" ? "destructive" : "default"}
            onClick={() => void handleConfirm()}
            disabled={!canConfirm}
          >
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {confirmLabel(change)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
